/**
 * „Kalendarz Apple", the second row of Integracje's „Kalendarz" group (owner, 28 October 2026; a
 * row since 4 November): a button on a Mac, iPhone or iPad that opens Calendar's own subscribe
 * dialog; with the feed on, „Utwórz nowy" and „Wyłącz link" beside it. The address is never drawn
 * (calendar-sync R10). No Apple mark — Apple does not license its icons for this — so the row leads
 * with an ordinary outline calendar in a neutral tile.
 */

import { useTranslation } from 'react-i18next';

import { instantWhen } from '~/shared/lib/instantWhen';
import { CalendarIcon } from '~/shared/ui/AuthIcons';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { FormBanner } from '~/shared/ui/FormBanner';

import type { AppleCalendarApi } from '../lib/useAppleCalendar';
import { SECONDARY_PILL } from './accountButtons';
import screen from './AccountScreen.module.css';
import styles from './CalendarTab.module.css';

const KEY = 'web:account.calendar.apple';

/** `apple` is the screen's `useAppleCalendar`: the group's „pick one" hint reads it too. */
export function AppleCalendarBlock({
  apple,
  className,
}: {
  readonly apple: AppleCalendarApi;
  readonly className: string;
}) {
  const { t, i18n } = useTranslation();
  const { view } = apple;
  if (view.kind === 'hidden') return null;

  /* The server only sees the feed being fetched, so „connected" means a calendar has fetched it. */
  const status = !view.enabled
    ? t(`${KEY}.note`)
    : view.lastFetchedAt === null
      ? t('web:account.calendar.statusPair', { first: t(`${KEY}.linkOn`), second: t(`${KEY}.fetchPending`) })
      : t('web:account.calendar.statusPair', {
          first: t(`${KEY}.connected`),
          second: t('web:account.calendar.lastSyncedInline', {
            when: instantWhen(Date.parse(view.lastFetchedAt), apple.answeredAt, i18n.language),
          }),
        });

  return (
    <>
      <div className={`${String(screen.groupRow)} ${className}`}>
        <span className={styles.tile} aria-hidden="true">
          <CalendarIcon className={styles.tileGlyph} />
        </span>
        <div className={screen.rowBody}>
          <b className={styles.title}>{t(`${KEY}.title`)}</b>
          <span className={styles.status}>{status}</span>
        </div>
        <div className={screen.rowActions}>
          {view.enabled ? (
            <>
              <button
                type="button"
                className={SECONDARY_PILL}
                aria-disabled={apple.busy ? 'true' : undefined}
                onClick={() => {
                  apple.onAsk('rotate');
                }}
              >
                {t(`${KEY}.rotate`)}
              </button>
              <button
                type="button"
                className={SECONDARY_PILL}
                aria-disabled={apple.busy ? 'true' : undefined}
                onClick={() => {
                  apple.onAsk('disable');
                }}
              >
                {t(`${KEY}.disable`)}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className={SECONDARY_PILL}
            aria-disabled={apple.busy ? 'true' : undefined}
            aria-busy={apple.busy}
            onClick={apple.onSubscribe}
          >
            {t(`${KEY}.add`)}
          </button>
        </div>
      </div>
      {apple.refusal !== null && (
        <div className={styles.rowBanner}>
          <FormBanner message={apple.refusal} />
        </div>
      )}
      <ConfirmDialog prompt={apple.prompt} onConfirm={apple.onConfirm} onCancel={apple.onCancel} />
    </>
  );
}
